import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CategoriesView, CategoriesViewProps, ThirdNavLeftButtons, ThirdNavRightButtons } from './Categories.view';
import {
  CategoryArchiveFilter,
  GetCategoriesLazyQueryHookResult,
  GetPostQueueCountLazyQueryHookResult,
  GetQueueMetadataLazyQueryHookResult,
  GetTimeslotsQuery,
  QueueContext,
  QueueFilterPostStatus
} from '../../../models/graphql';
import { useNavigate } from 'react-router-dom';
import { cloneDeep, filter } from 'lodash';
import { useThirdNavChildren } from '../../../hooks/useThirdNavChildren';
import { useDidMount } from 'hooks/useDidMount';
import { AppContext } from '../../../providers/AppProvider';
import { GetCategories_workspace_categories } from '../../../models/legacy-graphql';
import { getCategoryQueueCounts } from './Categories.helpers';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type CategoriesControllerProps = Omit<
  CategoriesViewProps,
  | 'loading'
  | 'getCategories'
  | 'categoriesResults'
  | 'openCreateEditCategoryModal'
  | 'setOpenCreateEditCategoryModal'
  | 'categories'
  | 'categoryFilterHandler'
  | 'counts'
> & {
  getCategories: GetCategoriesLazyQueryHookResult[0];
  categoriesResults: GetCategoriesLazyQueryHookResult[1];
  getQueueMetaData: GetQueueMetadataLazyQueryHookResult[0];
  queueMetadataResults: GetQueueMetadataLazyQueryHookResult[1];
  getPostQueueCount: GetPostQueueCountLazyQueryHookResult[0];
  postQueueCountResults: GetPostQueueCountLazyQueryHookResult[1];
  timeslots: GetTimeslotsQuery['workspace']['timeslots'];
};

export const CategoriesController = (props: CategoriesControllerProps): JSX.Element => {
  const navigate = useNavigate();
  const { selectedWorkspaceId, setLimitReachedModalState } = useContext(AppContext);
  const [openCreateEditCategoryModal, setOpenCreateEditCategoryModal] = useState<boolean>(false);
  const [counts, setCounts] = useState<Record<string | number, number | undefined>>({});

  const { canAddCategory } = useOrganizationPlan();

  const queueMetadata = React.useMemo(
    () => props.queueMetadataResults.data?.workspace.queueMeta || [],
    [props.queueMetadataResults]
  );
  const categoryFilterHandler = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const collator: Intl.Collator = new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base'
  });

  const categoriesUnFiltered = !props.isArchivedView
    ? cloneDeep(props.categoriesResults.data?.workspace.categories || []).sort(
        (a: GetCategories_workspace_categories, b: GetCategories_workspace_categories) =>
          a.name && b.name ? collator.compare(a.name, b.name) : collator.compare('', '')
      )
    : cloneDeep(props.categoriesResults.data?.workspace.categories || []).sort(
        (a: GetCategories_workspace_categories, b: GetCategories_workspace_categories) =>
          a.archivedDate < b.archivedDate ? 1 : a.archivedDate === b.archivedDate ? 1 : -1
      );
  const categories = filter(categoriesUnFiltered, ['archived', props.isArchivedView]);

  const thirdNavChildren = useMemo(
    () => ({
      left: ThirdNavLeftButtons(props.isArchivedView, categoryFilterHandler),
      right: ThirdNavRightButtons(() => {
        canAddCategory()
          ? setOpenCreateEditCategoryModal(true)
          : setLimitReachedModalState({
              isOpen: true,
              type: 'category'
            });
      })
    }),
    [
      props.isArchivedView,
      categoryFilterHandler,
      canAddCategory,
      setOpenCreateEditCategoryModal,
      setLimitReachedModalState
    ]
  );

  useThirdNavChildren(thirdNavChildren);

  useEffect(() => {
    if (props.postQueueCountResults.data?.workspace.queueCounts) {
      setCounts(getCategoryQueueCounts(props.postQueueCountResults.data.workspace.queueCounts));
    }
  }, [props.postQueueCountResults.data?.workspace.queueCounts]);

  const { getCategories } = props;
  useEffect(() => {
    getCategories({
      variables: {
        statusFilter: props.isArchivedView ? CategoryArchiveFilter.ARCHIVED : CategoryArchiveFilter.ACTIVE,
        workspaceId: selectedWorkspaceId
      }
    });
  }, [getCategories, props.isArchivedView, selectedWorkspaceId]);

  useDidMount(() => {
    if (selectedWorkspaceId) {
      Promise.all([
        props.getQueueMetaData({ variables: { workspaceId: selectedWorkspaceId } }),
        props.getPostQueueCount({
          variables: {
            workspaceId: selectedWorkspaceId as string,
            queueFilters: { status: QueueFilterPostStatus.SCHEDULED },
            queueContext: QueueContext.SCHEDULE
          }
        })
      ]);
    }
  });

  const viewProps: CategoriesViewProps = {
    ...props,
    loading: props.categoriesResults.loading,
    openCreateEditCategoryModal,
    setOpenCreateEditCategoryModal,
    categories: categories,
    queueMetadata,
    counts
  };

  return <CategoriesView {...viewProps} />;
};
